import React, { useReducer, useRef, useState } from 'react';

import { FormHelperText, IconButton, InputAdornment, Paper, Popper } from '@mui/material';
import type { PopperProps } from '@mui/material';
import type { DateFieldProps, StaticDatePickerProps } from '@mui/x-date-pickers';
import { DateField, StaticDatePicker } from '@mui/x-date-pickers';
import { pickersInputBaseClasses } from '@mui/x-date-pickers/PickersTextField';
import clsx from 'clsx';
import { useClickAway } from 'react-use';
import { getAlinePlaceholderCSSProps } from 'src/AlineInput';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { DateFieldIcon } from 'src/components/icons/DateFieldIcon';
import { ForwardArrowIcon } from 'src/components/icons/ForwardArrowIcon';
import type { ReactSetStateAction } from 'src/types';
import { makeStyles } from 'tss-react/mui';

// Normalize Invalid Date (`.getTime() === NaN`) to null so equality checks
// can't trip the NaN !== NaN trap and infinite-loop the remount logic below.
function getValidTime(date: Date | null): number | null {
  if (!date) return null;
  const t = date.getTime();
  return Number.isNaN(t) ? null : t;
}

interface StyleProps {
  error: boolean;
  disabled?: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { disabled, error }) => ({
  fullWidth: {
    width: '100%',
  },
  helperText: {
    textAlign: 'left',
  },
  inputRoot: {
    // pickersInputBaseClasses doesn't expose `sectionsContainer` at runtime
    // (it's only in the type), so we target the actual class name MUI applies
    // via its styled() name + slot.
    '& .MuiPickersInputBase-sectionsContainer': {
      '&::placeholder': {
        ...getAlinePlaceholderCSSProps(theme),
      },
      color: disabled ? theme.palette.text.disabled : theme.appColors.iconsType,
      fontSize: 14,
      padding: theme.spacing(1),
    },

    '&:focus-within': {
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `1px solid ${theme.appColors.primary}`,
    },
    [`& .${pickersInputBaseClasses.sectionContent}`]: {
      color: disabled ? theme.palette.text.disabled : theme.appColors.iconsType,
    },
    border: error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    display: 'flex',
    minHeight: theme.appSizes.inputHeight,
    position: 'relative',
  },
  popper: {
    zIndex: theme.zIndex.tooltip,
  },
}));

interface AlineDatePickerProps {
  onDateChange: (date: Date | null) => void;
  open: boolean;
  setOpen: ReactSetStateAction<boolean>;
  value: Date | null;
  alineInputRef?: React.Ref<HTMLInputElement>;
  dateFieldProps?: DateFieldProps;
  disabled?: boolean;
  enableFlip?: boolean;
  id?: string;
  inputClassName?: string;
  inputDataCy?: string;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  pickerProps?: StaticDatePickerProps;
  placement?: PopperProps['placement'];
}

export function AlineDatePicker(props: AlineDatePickerProps): JSX.Element {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [errorText, setErrorText] = useState<string>('');
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const {
    alineInputRef,
    dateFieldProps,
    disabled,
    enableFlip = false,
    id,
    inputClassName,
    inputDataCy,
    onBlur,
    onDateChange,
    onFocus,
    open,
    pickerProps,
    placement = 'bottom-start',
    setOpen,
    value,
  } = props;
  const { classes } = useStyles({ disabled, error: Boolean(errorText) });
  const fullWidth = dateFieldProps?.fullWidth ?? false;
  const inputDataAttrs: { 'data-cy'?: string } = inputDataCy ? { 'data-cy': inputDataCy } : {};
  // Extract conflict keys from caller props so we can compose/merge with
  // internal config instead of being silently clobbered or clobbering them.
  const {
    onChange: callerDateFieldOnChange,
    slotProps: callerDateFieldSlotProps,
    slots: callerDateFieldSlots,
    ...restDateFieldProps
  } = dateFieldProps ?? {};
  const callerTextFieldSlotProps =
    callerDateFieldSlotProps?.textField && typeof callerDateFieldSlotProps.textField === 'object'
      ? callerDateFieldSlotProps.textField
      : undefined;
  const {
    onAccept: callerOnAccept,
    onChange: callerOnChange,
    onClose: callerOnClose,
    onError: callerOnError,
    slots: callerPickerSlots,
    ...restPickerProps
  } = pickerProps ?? {};
  /**
   * MUI DateField resets its internal section editing state when value={null},
   * clearing dd/mm while the user types yyyy. We keep the DateField
   * uncontrolled (using defaultValue, never value), and force a remount when
   * the value changes from outside the DateField (e.g. calendar pick or
   * parent sync). `lastEmittedTimeRef` tracks the time we most recently
   * emitted from DateField's onChange so we can skip the remount on round-
   * trips back through the parent. Times are normalized via getValidTime to
   * avoid the NaN !== NaN trap on Invalid Date inputs.
   */
  const [fieldKey, remountField] = useReducer(x => x + 1, 0);
  const lastEmittedTimeRef = useRef<number | null>(getValidTime(value));
  const currentTime = getValidTime(value);
  if (lastEmittedTimeRef.current !== currentTime) {
    lastEmittedTimeRef.current = currentTime;
    remountField();
  }

  useClickAway(anchorRef, () => setOpen(false));

  return (
    <>
      <DateField
        {...restDateFieldProps}
        key={fieldKey}
        ref={setNode}
        color={errorText ? 'error' : 'primary'}
        defaultValue={value}
        disabled={disabled}
        slotProps={{
          ...callerDateFieldSlotProps,
          textField: {
            ...callerTextFieldSlotProps,
            className: fullWidth ? classes.fullWidth : undefined,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Open date picker"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  <DateFieldIcon />
                </IconButton>
              </InputAdornment>
            ),
            id,
            inputRef: alineInputRef,
            onBlur,
            onFocus,
            slotProps: {
              ...callerTextFieldSlotProps?.slotProps,
              formHelperText: { className: classes.helperText },
              input: {
                ...inputDataAttrs,
                className: clsx(classes.inputRoot, inputClassName),
                disableUnderline: true,
              },
            },
            variant: 'standard',
          },
        }}
        slots={callerDateFieldSlots}
        onChange={(date, context) => {
          if (!context.validationError) {
            lastEmittedTimeRef.current = getValidTime(date);
            onDateChange(date);
          }
          callerDateFieldOnChange?.(date, context);
        }}
      />
      {errorText && (
        <FormHelperText error className={classes.helperText}>
          {errorText}
        </FormHelperText>
      )}
      <Popper
        anchorEl={node}
        className={classes.popper}
        modifiers={[
          ...(enableFlip ? [] : [{ enabled: false, name: 'flip' }]),
          {
            enabled: false,
            name: 'preventOverflow',
          },
        ]}
        open={open}
        placement={placement}
      >
        {/**
         * StaticDatePicker never closes itself on a day pick (MUI only does that
         * for the popover pickers), so close the popup here. Day cells select
         * the date in their own click and Enter/Space handlers, then the event
         * bubbles up to this Paper. onChange can't be used: MUI skips it when
         * the picked day is already selected. Year and month picks use other
         * roles, so they keep the popup open.
         */}
        <Paper
          ref={anchorRef}
          onClick={event => {
            if (getIsDayCellEvent(event)) setOpen(false);
          }}
          onKeyDown={event => {
            if ((event.key === 'Enter' || event.key === ' ') && getIsDayCellEvent(event))
              setOpen(false);
          }}
        >
          <StaticDatePicker
            {...restPickerProps}
            // v9 keeps `displayStaticWrapperAs`. Mobile is the default; desktop
            // hides the toolbar AND widens the year grid (yearsPerRow 3 → 4).
            disabled={disabled}
            displayStaticWrapperAs="desktop"
            slots={{
              actionBar: () => null,
              leftArrowIcon: BackArrowIcon,
              rightArrowIcon: ForwardArrowIcon,
              ...callerPickerSlots,
            }}
            value={value}
            onAccept={(date, context) => {
              setOpen(false);
              callerOnAccept?.(date, context);
            }}
            onChange={(date, context) => {
              onDateChange(date);
              callerOnChange?.(date, context);
            }}
            onClose={() => {
              setOpen(false);
              callerOnClose?.();
            }}
            onError={(reason, value) => {
              switch (reason) {
                case 'invalidDate':
                  setErrorText('Please enter a valid date');
                  break;
                default:
                  setErrorText('');
              }
              callerOnError?.(reason, value);
            }}
          />
        </Paper>
      </Popper>
    </>
  );
}

/** True when the event came from a day button in the calendar grid, not a blank filler cell. */
function getIsDayCellEvent(event: React.SyntheticEvent): boolean {
  return (
    event.target instanceof Element && Boolean(event.target.closest('button[role="gridcell"]'))
  );
}
